import React, { useState, useEffect } from 'react';
// Script Path & Filename: C:/inetpub/Sapphire/frontend/src/components/RemoteSQLExplorer.jsx
import { DynamicTableViewer as DynamicTableViewerV2 } from './DynamicTableViewerV2';
import { VirtualSchemaManager } from './VirtualSchemaManager';
import { SearchableSelect } from './DynamicTableViewerV2/SearchableSelect';
import { PriceUpdaterModal } from './PriceUpdaterModal';

// Helper to build recursive tree of organizations
const buildOrgTree = (orgs, insts) => {
    const orgMap = {};
    orgs.forEach(org => {
        orgMap[org.OrganizationID] = {
            ...org,
            children: [],
            instances: []
        };
    });

    const unassignedOrg = {
        OrganizationID: 'Unassigned',
        OrganizationName: 'Unassigned',
        ParentOrganizationID: null,
        children: [],
        instances: []
    };

    const rootNodes = [];

    insts.forEach(inst => {
        const orgId = inst.OrganizationID;
        if (orgId && orgMap[orgId]) {
            orgMap[orgId].instances.push(inst);
        } else {
            unassignedOrg.instances.push(inst);
        }
    });

    orgs.forEach(org => {
        const node = orgMap[org.OrganizationID];
        if (node.ParentOrganizationID && orgMap[node.ParentOrganizationID]) {
            orgMap[node.ParentOrganizationID].children.push(node);
        } else {
            rootNodes.push(node);
        }
    });

    const filterEmptyAndSort = (nodes) => {
        const filtered = nodes.filter(node => {
            node.children = filterEmptyAndSort(node.children);
            return node.instances.length > 0 || node.children.length > 0;
        });
        filtered.sort((a, b) => a.OrganizationName.localeCompare(b.OrganizationName));
        filtered.forEach(node => {
            node.instances.sort((a, b) => (a.InstanceName || '').localeCompare(b.InstanceName || ''));
        });
        return filtered;
    };

    const result = filterEmptyAndSort(rootNodes);
    if (unassignedOrg.instances.length > 0) {
        result.push(unassignedOrg);
    }
    return result;
};

// Helper to group instances by DeviceID
const groupInstancesByDevice = (instances) => {
    if (!instances) return [];
    const deviceMap = {};
    instances.forEach(inst => {
        const deviceId = inst.DeviceID || `unknown-device-${inst.InstanceID}`;
        if (!deviceMap[deviceId]) {
            deviceMap[deviceId] = {
                DeviceID: inst.DeviceID || null,
                DeviceName: inst.DeviceName || 'Unknown Device',
                DeviceType: inst.DeviceType || '',
                DeviceRole: inst.DeviceRole || '',
                instances: []
            };
        }
        deviceMap[deviceId].instances.push(inst);
    });

    // Return list of devices sorted by DeviceName
    return Object.values(deviceMap).sort((a, b) => 
        (a.DeviceName || '').localeCompare(b.DeviceName || '')
    );
};

// Recursive component to display tree nodes
const OrgTreeNode = ({ node, level = 0, expandedGroups, onToggle, isSearching, connectToInstance, setAuthModal }) => {
    const isExpanded = isSearching ? true : !!expandedGroups[node.OrganizationID];
    const hasChildren = node.children.length > 0;
    const hasInstances = node.instances.length > 0;
    
    // Calculate nested indentation (level * 16px)
    const paddingLeft = level * 16 + 12;

    const handleInterfaceConnect = (instance, ipAddress) => {
        if (instance.HasCredentials) {
            connectToInstance(instance, ipAddress);
        } else {
            setAuthModal({ open: true, instance, username: '', password: '' });
        }
    };

    return (
        <div className="flex flex-col border-l border-slate-700/30" style={{ marginLeft: level > 0 ? '8px' : '0px' }}>
            {/* Organization Header */}
            <button 
                onClick={() => onToggle(node.OrganizationID)}
                style={{ paddingLeft: `${paddingLeft}px` }}
                className="w-full flex items-center justify-between p-3 bg-slate-800/80 hover:bg-slate-700/60 transition-colors border-b border-slate-700/50 text-left"
            >
                <div className="flex items-center">
                    <span className="material-symbols-outlined text-slate-400 mr-2 text-lg select-none">
                        {isExpanded ? 'expand_more' : 'chevron_right'}
                    </span>
                    <span className="font-bold text-slate-200">{node.OrganizationName}</span>
                </div>
                <span className="text-xs bg-slate-900 border border-slate-700 text-slate-400 px-2.5 py-0.5 rounded-full font-medium">
                    {node.instances.length} Instance{node.instances.length !== 1 ? 's' : ''}
                </span>
            </button>

            {/* Collapsible Content */}
            {isExpanded && (
                <div className="flex flex-col bg-slate-900/10">
                    {/* Render Child Organizations */}
                    {hasChildren && (
                        <div className="flex flex-col">
                            {node.children.map(child => (
                                <OrgTreeNode 
                                    key={child.OrganizationID} 
                                    node={child} 
                                    level={level + 1}
                                    expandedGroups={expandedGroups}
                                    onToggle={onToggle}
                                    isSearching={isSearching}
                                    connectToInstance={connectToInstance}
                                    setAuthModal={setAuthModal}
                                />
                            ))}
                        </div>
                    )}

                    {/* Render Devices directly in this Organization */}
                    {hasInstances && (
                        <div className="p-4 space-y-6">
                            {groupInstancesByDevice(node.instances).map(device => (
                                <div key={device.DeviceID || device.DeviceName} className="bg-slate-900/60 rounded-xl border border-slate-700/60 p-5 shadow-md flex flex-col space-y-4">
                                    {/* Device Header/Info */}
                                    <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-slate-800/80">
                                        <div className="flex items-center space-x-3">
                                            <span className="material-symbols-outlined text-blue-400 text-2xl select-none">router</span>
                                            <div>
                                                <h4 className="text-base font-extrabold text-slate-200">
                                                    {device.DeviceName || 'Unknown Device'}
                                                </h4>
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {device.DeviceType && (
                                                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-slate-800 border border-slate-700 text-slate-400 rounded">
                                                            Type: {device.DeviceType}
                                                        </span>
                                                    )}
                                                    {device.DeviceRole && (
                                                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-slate-800 border border-slate-700 text-slate-400 rounded">
                                                            Role: {device.DeviceRole}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SQL Instances Sub-cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {device.instances.map(instance => (
                                            <div key={instance.InstanceID} className="bg-slate-900 rounded-lg border border-slate-700 p-4 shadow-sm hover:border-blue-500/50 transition-colors flex flex-col justify-between">
                                                {/* Instance Header */}
                                                <div className="border-b border-slate-800 pb-3 mb-3">
                                                    <div className="flex justify-between items-start gap-2 mb-2">
                                                        <h5 className="text-sm font-bold text-slate-200 truncate" title={instance.InstanceName || `Instance ${instance.InstanceID}`}>
                                                            {instance.InstanceName || `Instance ${instance.InstanceID}`}
                                                        </h5>
                                                        
                                                        {/* Color-coded Auth Status Button */}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setAuthModal({ open: true, instance, username: instance.VaultUsername || '', password: '' });
                                                            }}
                                                            className={`text-[11px] px-2.5 py-1 rounded border flex items-center gap-1 shrink-0 transition-colors shadow-sm font-medium ${
                                                                instance.HasCredentials 
                                                                    ? 'bg-green-950/40 text-green-400 border-green-800/80 hover:bg-green-900/60 hover:text-green-300 hover:border-green-700' 
                                                                    : 'bg-red-950/40 text-red-400 border-red-800/80 hover:bg-red-900/60 hover:text-red-300 hover:border-red-700'
                                                            }`}
                                                            title={instance.HasCredentials ? "Edit Vault Credentials" : "Add Vault Credentials"}
                                                        >
                                                            <span className="material-symbols-outlined text-[13px]">
                                                                {instance.HasCredentials ? 'lock_open' : 'lock'}
                                                            </span>
                                                            {instance.HasCredentials ? 'Vaulted' : 'No Auth'}
                                                        </button>
                                                    </div>
                                                    
                                                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                                                        <span className="flex items-center gap-1"><span className="text-slate-500">Port:</span> <span className="font-mono text-slate-300">{instance.Port}</span></span>
                                                        <span className="flex items-center gap-1"><span className="text-slate-500">Engine:</span> <span className="text-slate-300 truncate max-w-[80px]" title={instance.EngineType}>{instance.EngineType}</span></span>
                                                        <span className="flex items-center gap-1"><span className="text-slate-500">DBs:</span> <span className="text-slate-300">{instance.DatabaseCount}</span></span>
                                                    </div>
                                                </div>

                                                {/* Interfaces with Connect Buttons */}
                                                <div className="space-y-2 mt-auto">
                                                    {instance.interfaces && instance.interfaces.length > 0 ? (
                                                        instance.interfaces.map((face, idx) => (
                                                            <div key={idx} className="flex items-center justify-between gap-2 bg-slate-950/40 p-2 rounded border border-slate-800/80">
                                                                <div className="flex flex-col min-w-0 flex-1">
                                                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider truncate" title={face.IPType}>
                                                                        {face.IPType}:
                                                                    </span>
                                                                    <span className="font-mono text-xs text-slate-300 select-all truncate" title={face.IPAddress}>
                                                                        {face.IPAddress}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleInterfaceConnect(instance, face.IPAddress)}
                                                                    className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold transition-colors flex items-center gap-1 shadow-sm shrink-0"
                                                                >
                                                                    <span className="material-symbols-outlined text-[14px]">login</span>
                                                                    Connect
                                                                </button>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <p className="text-xs text-slate-500 italic text-center py-2 bg-slate-950/20 rounded border border-slate-850">
                                                            No interfaces mapped
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const RemoteSQLExplorer = ({ token }) => {
    const [instances, setInstances] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uiConfig, setUiConfig] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});

    const [selectedInstance, setSelectedInstance] = useState(null);
    const [selectedIPAddress, setSelectedIPAddress] = useState('');
    const [databases, setDatabases] = useState([]);
    const [selectedDatabase, setSelectedDatabase] = useState('');
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('');
    const [dbGroups, setDbGroups] = useState({});
    const [explorerView, setExplorerView] = useState('explorer'); // 'explorer' or 'virtual'
    const [inlineTag, setInlineTag] = useState('');

    useEffect(() => {
        if (selectedDatabase) {
            setInlineTag(dbGroups[selectedDatabase.toLowerCase()] || '');
        } else {
            setInlineTag('');
        }
    }, [selectedDatabase, dbGroups]);

    const [authModal, setAuthModal] = useState({ open: false, instance: null, username: '', password: '' });
    const [savingAuth, setSavingAuth] = useState(false);

    useEffect(() => {
        loadInstances();
        loadUiConfig();
        loadDbGroups();
    }, [token]);

    const loadDbGroups = async () => {
        try {
            const res = await fetch('/api/virtual-schema/databases', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const mapping = {};
                data.forEach(db => {
                    if (db.SchemaGroup) {
                        mapping[db.DatabaseName.toLowerCase()] = db.SchemaGroup;
                    }
                });
                setDbGroups(mapping);
            }
        } catch (err) {
            console.error("Failed to load database schema groups", err);
        }
    };

    const loadUiConfig = async () => {
        try {
            const res = await fetch('/api/data/uiconfig', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUiConfig(data);
            }
        } catch (err) {
            console.warn('Could not load UIConfig for remote label resolution:', err.message);
        }
    };

    const loadInstances = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/api/remote-sql/instances', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch SQL instances.');
            const data = await res.json();
            setInstances(data.instances || []);
            setOrganizations(data.organizations || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAuth = async () => {
        setSavingAuth(true);
        try {
            const res = await fetch(`/api/remote-sql/${authModal.instance.InstanceID}/credentials`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ Username: authModal.username, Password: authModal.password })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save credentials.');
            }
            setAuthModal({ open: false, instance: null, username: '', password: '' });
            await loadInstances();
        } catch (err) {
            alert(`Auth Error: ${err.message}`);
        } finally {
            setSavingAuth(false);
        }
    };

    const connectToInstance = async (instance, ipAddress) => {
        setError(null);
        try {
            const res = await fetch(`/api/remote-sql/${instance.InstanceID}/${ipAddress}/databases`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || 'Connection to remote instance failed.');
            }
            const data = await res.json();
            setDatabases(data);
            setSelectedInstance(instance);
            setSelectedIPAddress(ipAddress);
            setSelectedDatabase('');
            setSelectedTable('');
            setTables([]);
        } catch (err) {
            alert(`Connection Error: ${err.message}`);
        }
    };

    const handleDatabaseSelect = async (dbName) => {
        setSelectedDatabase(dbName);
        setSelectedTable('');
        if (!dbName) {
            setTables([]);
            return;
        }

        try {
            const res = await fetch(`/api/remote-sql/${selectedInstance.InstanceID}/${selectedIPAddress}/${dbName}/schema/tables`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch remote tables.');
            const data = await res.json();
            setTables(data.map(t => t.TableName));
            if (data.length > 0) setSelectedTable(data[0].TableName);
        } catch (err) {
            alert(`Schema Error: ${err.message}`);
            setTables([]);
        }
    };

    const disconnect = () => {
        setSelectedInstance(null);
        setSelectedIPAddress('');
        setSelectedDatabase('');
        setSelectedTable('');
        setDatabases([]);
        setTables([]);
    };

    if (loading) return <div className="p-4 text-slate-400">Scanning network instances...</div>;
    if (error) return <div className="p-4 text-red-400">Error: {error}</div>;

    if (selectedInstance) {
        const group = selectedDatabase ? dbGroups[selectedDatabase.toLowerCase()] : null;
        const viewKey = group ? `${group}_${selectedTable}` : (selectedDatabase ? `${selectedInstance.InstanceID}_${selectedDatabase}_${selectedTable}` : selectedTable);

        return (
            <div className="flex flex-col h-full space-y-4">
                <div className="flex items-center space-x-4 bg-slate-800 p-4 rounded-lg border border-slate-700 shrink-0 shadow">
                    <button onClick={disconnect} className="text-slate-400 hover:text-white mr-2 transition-colors" title="Disconnect and return to instances">
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <div className="flex-1 flex gap-4 items-end">
                        <div className="flex-1">
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Remote Database</label>
                            <SearchableSelect 
                                value={selectedDatabase} 
                                onChange={e => handleDatabaseSelect(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-4 py-2 text-white outline-none focus:border-blue-500 text-sm"
                                placeholder="-- Select Target Database --"
                                options={databases.map(db => ({ value: db.name, label: db.name }))}
                            />
                        </div>
                        {selectedDatabase && (
                            <div className="flex-1 min-w-[150px]">
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Schema Group Tag</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text" 
                                        placeholder="e.g. pbsdata" 
                                        value={inlineTag}
                                        onChange={e => setInlineTag(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white outline-none focus:border-blue-500 text-sm"
                                    />
                                    <button 
                                        onClick={async () => {
                                            const headers = { 
                                                'Authorization': `Bearer ${token}`,
                                                'Content-Type': 'application/json'
                                            };
                                            try {
                                                const res = await fetch(`/api/virtual-schema/databases/${selectedInstance.InstanceID}/${selectedDatabase}/tag`, {
                                                    method: 'PUT',
                                                    headers,
                                                    body: JSON.stringify({ schemaGroup: inlineTag.trim() || null })
                                                });
                                                if (res.ok) {
                                                    await loadDbGroups();
                                                } else {
                                                    const err = await res.json();
                                                    alert(`Tag Error: ${err.error}`);
                                                }
                                            } catch (e) {
                                                alert(`Network Error: ${e.message}`);
                                            }
                                        }}
                                        disabled={inlineTag.trim() === (dbGroups[selectedDatabase.toLowerCase()] || '')}
                                        className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-600 text-white font-semibold px-3 py-2 rounded text-xs transition-colors shrink-0"
                                    >
                                        Tag
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="flex space-x-1 bg-slate-900/50 p-1 rounded border border-slate-700 shrink-0">
                            <button 
                                onClick={() => setExplorerView('explorer')}
                                className={`px-4 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition-colors flex items-center ${explorerView === 'explorer' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                            >
                                <span className="material-symbols-outlined text-[16px] mr-1.5">table_rows</span>
                                DB Admin
                            </button>
                            <button 
                                onClick={() => setExplorerView('virtual')}
                                className={`px-4 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition-colors flex items-center ${explorerView === 'virtual' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                            >
                                <span className="material-symbols-outlined text-[16px] mr-1.5">settings_suggest</span>
                                Virtual Schema
                            </button>
                            {inlineTag === 'pbsdata' && (
                                <button 
                                    onClick={() => setExplorerView('price-updater')}
                                    className={`px-4 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition-colors flex items-center ${explorerView === 'price-updater' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                >
                                    <span className="material-symbols-outlined text-[16px] mr-1.5">price_change</span>
                                    Price Update
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden min-h-0 min-w-0 bg-slate-800 rounded-lg border border-slate-700 flex flex-col shadow">
                    {explorerView === 'explorer' ? (
                        selectedDatabase ? (
                            <DynamicTableViewerV2 
                                targetTable={selectedTable}
                                token={token}
                                uiConfig={uiConfig}
                                apiPrefix={`/api/remote-sql/${selectedInstance.InstanceID}/${selectedIPAddress}/${selectedDatabase}`}
                                viewKey={viewKey}
                                onTableChange={setSelectedTable}
                            />
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-slate-500">
                                Select a database to explore the remote schema.
                            </div>
                        )
                    ) : explorerView === 'virtual' ? (
                        <VirtualSchemaManager 
                            token={token} 
                            instance={selectedInstance}
                            databaseName={selectedDatabase}
                            ipAddress={selectedIPAddress}
                            tableName={selectedTable}
                            schemaGroup={inlineTag}
                        />
                    ) : explorerView === 'price-updater' ? (
                        <PriceUpdaterModal 
                            token={token}
                            instanceId={selectedInstance?.InstanceID}
                            ipAddress={selectedIPAddress}
                            database={selectedDatabase}
                            onClose={() => setExplorerView('explorer')}
                        />
                    ) : null}
                </div>
            </div>
        );
    }

    const filteredInstances = instances.filter(i => {
        const search = searchTerm.toLowerCase();
        return (
            (i.InstanceName && i.InstanceName.toLowerCase().includes(search)) ||
            (i.DeviceName && i.DeviceName.toLowerCase().includes(search)) ||
            (i.DeviceType && i.DeviceType.toLowerCase().includes(search)) ||
            (i.DeviceRole && i.DeviceRole.toLowerCase().includes(search)) ||
            (i.interfaces && i.interfaces.some(face => 
                (face.IPAddress && face.IPAddress.toLowerCase().includes(search)) ||
                (face.IPType && face.IPType.toLowerCase().includes(search))
            ))
        );
    });

    const orgTree = buildOrgTree(organizations, filteredInstances);

    const toggleGroup = (orgId) => {
        setExpandedGroups(prev => ({ ...prev, [orgId]: !prev[orgId] }));
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 text-slate-300 min-w-0">
            <div className="flex items-center justify-between bg-slate-800 p-4 rounded-lg border border-slate-700 shrink-0 shadow">
                <div className="flex items-center space-x-4">
                    <span className="material-symbols-outlined text-blue-400 text-3xl">dns</span>
                    <div>
                        <h2 className="text-xl font-bold text-white">Remote SQL Explorer</h2>
                        <p className="text-sm text-slate-400">Select a vaulted SQL instance to dynamically query its schema and data.</p>
                    </div>
                </div>
                <div className="w-72">
                    <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
                        <input 
                            type="text" 
                            placeholder="Search instances, devices..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-600 rounded-full pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-blue-500 transition-colors"
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
                {orgTree.map(node => (
                    <OrgTreeNode 
                        key={node.OrganizationID} 
                        node={node} 
                        expandedGroups={expandedGroups}
                        onToggle={toggleGroup}
                        isSearching={searchTerm.trim() !== ''}
                        connectToInstance={connectToInstance}
                        setAuthModal={setAuthModal}
                    />
                ))}

                {orgTree.length === 0 && (
                    <div className="text-center p-12 text-slate-500 bg-slate-800/50 rounded-lg border border-slate-700 border-dashed">
                        No instances match your search criteria.
                    </div>
                )}
            </div>

            {/* Credential Management Modal */}
            {authModal.open && (
                <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="material-symbols-outlined text-blue-400 text-2xl">shield_locked</span>
                            <h2 className="text-xl font-bold text-white">Vault Credentials</h2>
                        </div>
                        
                        <p className="text-sm text-slate-400 mb-6">
                            Manage standard SQL authentication credentials for the instance <strong>{authModal.instance.InstanceName || authModal.instance.interfaces?.[0]?.IPAddress || 'SQL Instance'}</strong>.
                        </p>
                        
                        <div className="space-y-4 mb-8">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">SQL Username</label>
                                <input 
                                    type="text" 
                                    value={authModal.username} 
                                    onChange={e => setAuthModal({...authModal, username: e.target.value})}
                                    className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white outline-none focus:border-blue-500 transition-colors"
                                    placeholder="e.g., sa"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">SQL Password</label>
                                <input 
                                    type="password" 
                                    value={authModal.password} 
                                    onChange={e => setAuthModal({...authModal, password: e.target.value})}
                                    className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white outline-none focus:border-blue-500 transition-colors"
                                    placeholder={authModal.instance.HasCredentials ? "•••••••• (Leave blank to keep existing)" : "Enter secure password"}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button 
                                onClick={() => setAuthModal({ open: false, instance: null, username: '', password: '' })}
                                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition-colors border border-slate-600"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSaveAuth}
                                disabled={savingAuth}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded font-bold transition-colors shadow flex items-center"
                            >
                                {savingAuth ? 'Securing...' : 'Save & Vault'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};